import { Component, DestroyRef, Inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { BreadcrumbNavComponent } from '@bioconnect-ui/breadcrumb-nav';
import { DrawerModule } from 'primeng/drawer';
import { ToolbarModule } from 'primeng/toolbar';
import { ToastModule } from 'primeng/toast';
import { PopoverModule } from 'primeng/popover';
import { ButtonModule } from 'primeng/button';
import { AuthService } from '@auth0/auth0-angular';
import { DOCUMENT, AsyncPipe } from '@angular/common';
import { NavigationEnd, Router, RouterModule, RouterOutlet } from '@angular/router';
import { filter } from 'rxjs/operators';
import { PageStateService } from '@bioconnect-ui/shared-service';
import { UserService } from '@bioconnect-ui/user-service';
import { MenubarModule } from 'primeng/menubar';
import { MenuModule } from 'primeng/menu';
import { MenuItem } from 'primeng/api';

@Component({
  standalone: true,
  imports: [
    RouterOutlet,
    AsyncPipe,
    BreadcrumbNavComponent,
    ToolbarModule,
    ToastModule,
    ButtonModule,
    DrawerModule,
    PopoverModule,
    MenubarModule,
    MenuModule,
    RouterModule,
  ],
  selector: 'bioconnect-ui-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.css'],
})
export class AppComponent {
  title = 'bioconnect-portal';
  overrideScreenSize = false;
  userIsCurator = false;
  isHomePage = true;
  private currentRootSegment = '';

  menuItems: MenuItem[] = this.buildMenuItems(false);
  userMenuItems: MenuItem[] = [

  ];

  manualResize(value: boolean) {
    this.overrideScreenSize = value;
  }

  constructor(
    @Inject(DOCUMENT) public document: Document,
    public auth: AuthService,
    private userService: UserService,
    private router: Router,
    private pageStateService: PageStateService,
    private destroyRef: DestroyRef
  ) {
    this.updateRouteState(this.router.url);

    this.userService.getUserDetails()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(user => {
        this.userIsCurator = user.is_curator;
        this.menuItems = this.buildMenuItems(this.userIsCurator);
      });

    this.router.events
      .pipe(
        filter(event => event instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((event) => {
        this.updateRouteState((event as NavigationEnd).urlAfterRedirects);
      });

    this.auth.error$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((err: unknown) => {
        const authError = err as { error?: string; error_description?: string; message?: string };
        const error = authError.error;
        const description = authError.error_description || authError.message || '';

        if (
          error === 'access_denied' &&
          description.includes('ORCID_NOT_ALLOWED')
        ) {
          this.auth.logout({
            logoutParams: {
              returnTo: `${this.document.location.origin}/?error=orcid_not_allowed`,
            },
          });
        }
      });
  }

  login() {
    this.auth.loginWithRedirect();
  }

  private updateRouteState(url: string): void {
    const normalizedUrl = (url || '').split('?')[0].split('#')[0];
    const rootSegment = normalizedUrl.split('/')[1] ?? '';

    this.isHomePage = rootSegment === '' || rootSegment === 'portal-home';

    // Compare the route to the previous route, and if the root has changed, clear all saved page states.
    if (rootSegment !== this.currentRootSegment) {
      this.currentRootSegment = rootSegment;
      this.pageStateService.clearAll();
    }
  }

  private buildMenuItems(userIsCurator: boolean): MenuItem[] {
    const menuItems: MenuItem[] = [
      {
        label: 'Explore the Catalog',
        command: () => {
          this.router.navigate(['/search']);
        }
      },
      {
        label: 'Contribute',
        items: [
          {
            label: 'Studies',
            command: () => {
              this.router.navigate(['/curation']);
            }
          },
          {
            label: 'Files',
            command: () => {
              this.router.navigate(['/files']);
            }
          },
          {
            label: 'File Types',
            command: () => {
              this.router.navigate(['/file-type']);
            }
          },
          {
            label: 'Protocols',
            command: () => {
              this.router.navigate(['/protocols']);
            }
          },
        ]
      },
    ];

    if (userIsCurator) {
      menuItems.push({
        label: 'Admin',
        items: [
          {
            label: 'Metadata',
            command: () => {
              this.router.navigate(['/admin']);
            }
          },
          {
            label: 'Measures',
            command: () => {
              this.router.navigate(['/measures']);
            }
          },
          {
            label: 'Sample Templates',
            command: () => {
              this.router.navigate(['/biosamples/template-list']);
            }
          }
        ]
      });
    }


    menuItems.push(
      {
        label: 'Data Packages',
        command: () => {
          this.router.navigate(['/data-packages']);
        }
      },
      {
        label: 'Help',
        command: () => {
          this.router.navigate(['/help']);
        }
      }
    );

    return menuItems;
  }
}
